import { NavLink } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import BrandLogo from './brand/BrandLogo';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import LandingCustomerAccount from './LandingCustomerAccount';

const DEFAULT_PALETTE = {
  primary: '#02142C',
  accent: '#FF7300',
  onPrimary: '#FFFFFF'
};

const SINGLE_PAGE_LINKS = [
  { label: 'Home', href: '/#hero' },
  { label: 'About', href: '/#about' },
  { label: 'Features', href: '/#features' },
  { label: 'Pricing', href: '/#pricing' },
  { label: 'Find Food', href: '/#find-food' },
  { label: 'Contact', href: '/#contact' }
];

const normalizePublicLinks = (links = []) => {
  if (!links.length) return SINGLE_PAGE_LINKS;
  const sectionByPath = {
    '/': '#hero',
    '/about': '#about',
    '/features': '#features',
    '/pricing': '#pricing'
  };
  const normalized = links
    .map((item) => {
      const target = item.href || item.to || '/';
      const section = sectionByPath[target];
      if (!section) return item;
      return { ...item, href: `/${section}`, to: undefined };
    })
    .filter((item) => ['Home', 'About', 'Features', 'Pricing'].includes(item.label));
  return [
    ...normalized,
    { label: 'Find Food', href: '/#find-food' },
    { label: 'Contact', href: '/#contact' }
  ];
};

const LandingHeader = ({ links = [], palette = DEFAULT_PALETTE }) => {
  const resolvedPalette = {
    ...DEFAULT_PALETTE,
    ...(palette || {})
  };
  const user = useSelector(state => state.auth.user);
  const isCustomer = Boolean(user && ['CUSTOMER', 'USER'].includes(user.role));
  const location = useLocation();
  const [accountOpen, setAccountOpen] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const topTextColor = resolvedPalette.text || resolvedPalette.primary || DEFAULT_PALETTE.primary;
  const navTextColor = isScrolled || isMobileOpen ? resolvedPalette.onPrimary : topTextColor;
  const navBackground = isScrolled || isMobileOpen ? resolvedPalette.primary : 'transparent';
  const navShadow = isScrolled || isMobileOpen ? '0 10px 30px rgba(2, 20, 44, 0.14)' : 'none';
  const ctaBackground = isScrolled || isMobileOpen ? resolvedPalette.accent : resolvedPalette.primary;
  const ctaColor = isScrolled || isMobileOpen ? resolvedPalette.primary : resolvedPalette.onPrimary;
  const navItemsWithDefault = normalizePublicLinks(links);
  const burgerLine = useMemo(
    () => ({
      base: {
        width: 26,
        height: 2,
        background: navTextColor,
        borderRadius: 2,
        transition: 'transform 0.2s ease, opacity 0.2s ease'
      }
    }),
    [navTextColor]
  );

  useEffect(() => { setIsMobileOpen(false); setAccountOpen(false); }, [location.pathname, location.hash]);

  useEffect(() => {
    const updateScrolled = () => setIsScrolled(window.scrollY > 10);
    updateScrolled();
    window.addEventListener('scroll', updateScrolled, { passive: true });
    return () => window.removeEventListener('scroll', updateScrolled);
  }, []);

  const containerStyle = {
    maxWidth: 1200,
    margin: '0 auto',
    padding: '0 20px',
    height: 76,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'flex-start',
    gap: 12
  };

  return (
    <>
      <style>{`
        .landing-header .public-header-user-initial { padding: 0; width: 42px; height: 42px; min-width: 42px; border-radius: 50%; }
        .landing-header .public-account-dropdown-menu { z-index: 1080; }
        @media (max-width: 1199px) {
          .landing-header .max-container { justify-content: space-between !important; }
          .landing-header-brand { min-width: 0 !important; flex-shrink: 1; }
        }
        @media (max-width: 420px) {
          .landing-header .max-container { padding: 0 12px !important; gap: 8px !important; }
          .landing-header-brand { max-width: calc(100% - 100px); overflow: hidden; }
          .landing-header-brand svg { max-width: 100%; }
        }
        .nav-link-landing {
          color: ${navTextColor};
          text-decoration: none;
          position: relative;
          padding: 0.45rem 0.85rem;
          font-weight: 600;
          font-size: 14px;
          letter-spacing: 0.2px;
          transition: color 0.25s ease;
        }
        .nav-link-landing:hover { color: ${resolvedPalette.accent}; }
        .nav-link-landing::after {
          content: '';
          position: absolute;
          width: 0;
          height: 2px;
          bottom: -4px;
          left: 0;
          background-color: ${resolvedPalette.accent};
          transition: width 0.25s ease;
        }
        .nav-link-landing:hover::after { width: 100%; }
        @media (max-width: 1199px) {
          .mobile-nav .nav-link-landing {
            display: block;
            padding: 10px 0;
          }
          .mobile-nav .nav-link-landing::after {
            width: 0;
            opacity: 0;
          }
        }
      `}</style>
      <nav
        className="w-100 landing-header"
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 1060,
          background: navBackground,
          boxShadow: navShadow,
          transition: 'background 0.25s ease, box-shadow 0.25s ease'
        }}
      >
        <div className="max-container" style={containerStyle}>
          <div
            className="d-flex align-items-center gap-2 landing-header-brand"
            style={{
              minWidth: 238
            }}
          >
            <BrandLogo height={42} onDark={isScrolled || isMobileOpen} showTagline />
          </div>
          <div className="d-none d-xl-flex align-items-center gap-1 flex-grow-1 ms-2">
            {navItemsWithDefault.map((item) => {
              if (item.href) {
                return (
                  <a key={item.label} className="nav-link-landing" href={item.href}>
                    {item.label}
                  </a>
                );
              }
              return (
                <NavLink key={item.label} className="nav-link-landing" to={item.to || item.href || '/'}>
                  {item.label}
                </NavLink>
              );
            })}
          </div>
          {!isCustomer && <div className="d-none d-xl-flex align-items-center gap-3 ms-auto">
            <NavLink to="/auth/login" className="fw-semibold nav-link-landing" style={{ paddingRight: 0 }}>
              Log In
            </NavLink>
            <NavLink
              to="/shops/register"
              className="btn btn-sm"
              style={{ background: ctaBackground, color: ctaColor, fontWeight: 700, borderRadius: 9999, borderColor: ctaBackground }}
            >
              Get Started
            </NavLink>
          </div>}
          {isCustomer && <div className="landing-customer-account ms-auto"><LandingCustomerAccount palette={resolvedPalette} open={accountOpen} onToggle={open => { setAccountOpen(open); if (open) setIsMobileOpen(false); }} /></div>}
          <button
            type="button"
            className="d-xl-none border-0 bg-transparent"
            aria-label="Toggle navigation"
            aria-expanded={isMobileOpen}
            aria-controls="landing-mobile-navigation"
            onClick={() => { setIsMobileOpen(prev => !prev); setAccountOpen(false); }}
            style={{ width: 36, height: 36, display: 'grid', placeItems: 'center' }}
          >
            <div
              style={{
                position: 'relative',
                width: 26,
                height: 20
              }}
            >
              <div
                style={{
                  ...burgerLine.base,
                  position: 'absolute',
                  top: isMobileOpen ? '50%' : 0,
                  left: 0,
                  transform: isMobileOpen ? 'translateY(-50%) rotate(45deg)' : 'translateY(0) rotate(0)'
                }}
              />
              <div
                style={{
                  ...burgerLine.base,
                  position: 'absolute',
                  top: '50%',
                  left: 0,
                  opacity: isMobileOpen ? 0 : 1,
                  transform: 'translateY(-50%)'
                }}
              />
              <div
                style={{
                  ...burgerLine.base,
                  position: 'absolute',
                  bottom: isMobileOpen ? '50%' : 0,
                  left: 0,
                  transform: isMobileOpen ? 'translateY(50%) rotate(-45deg)' : 'translateY(0) rotate(0)'
                }}
              />
            </div>
          </button>
        </div>
      </nav>
      {isMobileOpen && (
        <div
          id="landing-mobile-navigation"
          className="d-xl-none mobile-nav"
          style={{
            position: 'fixed',
            top: 72,
            left: 0,
            right: 0,
            background: resolvedPalette.primary,
            zIndex: 1059,
            padding: '12px 20px',
            boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
          }}
        >
          <div className="d-flex flex-column gap-2">
            {navItemsWithDefault.map((item) =>
              item.href ? (
                <a key={item.label} className="nav-link-landing" href={item.href} onClick={() => setIsMobileOpen(false)}>
                  {item.label}
                </a>
              ) : (
                <NavLink key={item.label} className="nav-link-landing" to={item.to || item.href || '/'} onClick={() => setIsMobileOpen(false)}>
                  {item.label}
                </NavLink>
              )
            )}
            {!isCustomer && <><NavLink className="nav-link-landing" to="/auth/login" onClick={() => setIsMobileOpen(false)}>
              Log In
            </NavLink>
            <NavLink className="nav-link-landing" to="/shops/register" onClick={() => setIsMobileOpen(false)}>
              Get Started
            </NavLink></>}
          </div>
        </div>
      )}
    </>
  );
};

export default LandingHeader;
